import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Grid,
  CircularProgress,
  FormControlLabel,
  Switch,
  Divider,
  Chip,
} from '@mui/material';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { UnitOfMeasure, UnitOfMeasureFormData, commonUnitOfMeasures } from '@/types/unitOfMeasure';

// Mock data - replace with API calls
const mockUnitOfMeasure: UnitOfMeasure = {
  id: '1',
  code: 'EA',
  name: 'Each',
  description: 'Individual unit',
  isActive: true,
  createdAt: '2023-01-15T10:30:00Z',
  updatedAt: '2023-01-15T10:30:00Z',
};

const UnitOfMeasureForm: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);
  const isViewMode = location.pathname.includes('/view');
  const [loading, setLoading] = useState(isEditMode || isViewMode);
  const [submitting, setSubmitting] = useState(false);
  
  const [formData, setFormData] = useState<UnitOfMeasureFormData>({
    code: '',
    name: '',
    description: '',
    isActive: true,
  });

  const [errors, setErrors] = useState<Partial<Record<keyof UnitOfMeasureFormData, string>>>({});

  // Load unit of measure data in edit/view mode
  useEffect(() => {
    if (isEditMode || isViewMode) {
      const fetchUnitOfMeasure = async () => {
        setLoading(true);
        try {
          // Simulate API call
          await new Promise(resolve => setTimeout(resolve, 500));
          
          // In a real app, you would fetch the unit of measure by ID
          const fetchedUnitOfMeasure = mockUnitOfMeasure; // Replace with API call
          
          setFormData({
            code: fetchedUnitOfMeasure.code,
            name: fetchedUnitOfMeasure.name,
            description: fetchedUnitOfMeasure.description || '',
            isActive: fetchedUnitOfMeasure.isActive,
          });
          
        } catch (error) {
          console.error('Error fetching unit of measure:', error);
          // Handle error (e.g., show error message, redirect, etc.)
        } finally {
          setLoading(false);
        }
      };

      fetchUnitOfMeasure();
    }
  }, [id, isEditMode, isViewMode]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    const fieldName = name as keyof UnitOfMeasureFormData;
    
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
    
    // Clear error when field is edited
    if (errors[fieldName]) {
      setErrors(prev => ({
        ...prev,
        [fieldName]: ''
      }));
    }
  };

  const handleSelectChange = (e: React.ChangeEvent<{ name?: string; value: unknown }>) => {
    const name = e.target.name as keyof UnitOfMeasureFormData;
    const value = e.target.value as string;
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when field is edited
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleStatusChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      isActive: e.target.checked
    }));
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof UnitOfMeasureFormData, string>> = {};
    
    if (!formData.code.trim()) {
      newErrors.code = 'Code is required';
    }
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setSubmitting(true);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // In a real app, you would save the data to your API here
      console.log('Form data:', formData);
      
      // Navigate back to the list after successful submission
      navigate('/settings/unit-of-measures');
      
    } catch (error) {
      console.error('Error saving unit of measure:', error);
      // Handle error (e.g., show error message)
    } finally {
      setSubmitting(false);
    }
  };

  const handleSelectCommonUnit = (unit: string) => {
    setFormData(prev => ({
      ...prev,
      name: unit,
      code: unit.toUpperCase().substring(0, 3)
    }));
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" component="h1">
          {isViewMode ? 'View' : isEditMode ? 'Edit' : 'Add'} Unit of Measure
        </Typography>
      </Box>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box mb={3}>
          <Typography variant="subtitle1" gutterBottom>
            Common Units of Measure
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
            {commonUnitOfMeasures.map(unit => (
              <Chip
                key={unit}
                label={unit}
                onClick={() => handleSelectCommonUnit(unit)}
                variant="outlined"
                sx={{ cursor: 'pointer' }}
              />
            ))}
          </Box>
          <Divider sx={{ my: 2 }} />
        </Box>

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Code"
                name="code"
                value={formData.code}
                onChange={handleInputChange}
                error={Boolean(errors.code)}
                helperText={errors.code}
                required
                disabled={isViewMode}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                error={Boolean(errors.name)}
                helperText={errors.name}
                required
                disabled={isViewMode}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                multiline
                rows={3}
                disabled={isViewMode}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl 
                fullWidth 
                margin="normal" 
                required
                error={Boolean(errors.isActive)}
                disabled={isViewMode}
              >
                <InputLabel>Status</InputLabel>
                <Select
                  name="isActive"
                  value={formData.isActive ? 'active' : 'inactive'}
                  onChange={(e) => {
                    const isActive = e.target.value === 'active';
                    setFormData(prev => ({
                      ...prev,
                      isActive
                    }));
                  }}
                  label="Status"
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
                {errors.isActive && (
                  <FormHelperText>{errors.isActive}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            {!isViewMode && (
              <Grid item xs={12} sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/settings/unit-of-measures')}
                  disabled={submitting}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={submitting}
                >
                  {submitting ? <CircularProgress size={24} /> : isEditMode ? 'Update' : 'Save'}
                </Button>
              </Grid>
            )}
          </Grid>
        </form>
      </Paper>
    </Box>
  );
};

export default UnitOfMeasureForm;
